import moment from "moment";
import { responseMiddleware } from "services/alert";
import { alertRequest, body, request } from "services/request";
import { getProductMetricHeaders, type ProductMetricHeaders } from "sources/eventLogs";

const resource = "api/account";

const requestBody = (body: {}, headers: Record<string, string> = {}): RequestInit => ({
    body: JSON.stringify(body),
    method: "POST",
    cache: "default",
    headers: {
        "X-Device-Id": localStorage.getItem("deviceId") ?? "",
        "X-Requested-With": "XMLHttpRequest",
        "Content-Type": "application/json; charset=utf-8",
        ...headers,
    },
});

type RegisterOptions = {
    productMetricHeaders?: ProductMetricHeaders;
    returnErrorText?: boolean;
    throwOnError?: boolean;
};

const register = (body: {}, options: RegisterOptions = {}) =>
    fetch(`/${resource}/register`, requestBody(body, getProductMetricHeaders(options.productMetricHeaders ?? {})))
        .then((res) => responseMiddleware(res, { returnErrorText: options.returnErrorText }))
        .catch((err) => {
            console.error({ type: "error", message: "Неудачный запрос", title: "Ошибка.", err });

            if (options.throwOnError) {
                throw err;
            }
        });

const forgotPassword = (body: {}) =>
    fetch(`/${resource}/forgotPassword`, requestBody(body))
        .then((res) => responseMiddleware(res, {}))
        .catch((err) => console.error({ type: "error", message: "Неудачный запрос", title: "Ошибка.", err }));

const resetPassword = (body: {}) =>
    fetch(`/${resource}/resetPassword`, requestBody(body))
        .then((res) => responseMiddleware(res, {}))
        .catch((err) => console.error({ type: "error", message: "Неудачный запрос", title: "Ошибка.", err }));

const acceptInvitation = (body: {}) =>
    fetch(`/${resource}/acceptInvitation`, requestBody(body))
        .then((res) => responseMiddleware(res, {}))
        .catch((err) => console.error({ type: "error", message: "Неудачный запрос", title: "Ошибка.", err }));

const unsubscribeNotifications = (token: string) =>
    fetch(`/api/notifications/unsubscribe?token=${token}`, { ...requestBody(body), method: "GET" })
        .then((res) => responseMiddleware(res, {}))
        .catch((err) => console.error({ type: "error", message: "Неудачный запрос", title: "Ошибка.", err }));

const confirmEmail = (userId: string, token: string) =>
    fetch(`/${resource}/confirmEmail?userId=${userId}&token=${token}`, { ...requestBody(body), method: "GET" })
        .then((res) => responseMiddleware(res, {}))
        .catch((err) => console.error({ type: "error", message: "Неудачный запрос", title: "Ошибка.", err }));

const logout = () => request(`${resource}/logoff`, {});

const purgeOtherSessions = () => alertRequest(`${resource}/purgeOtherSessions`, {});

const loginResponseHandler = (response: {
    status: number;
    message: string;
    accessToken: { token: string; expiresIn: string };
    refreshToken: string;
    currentUser: { organizationId?: number };
    requiresTwoFactor: boolean;
    twoFactorProvider: string;
    twoFactorToken: string;
}) => {
    const { status } = response;
    if (status === 498) {
        throw new Error("License expired");
    }
    const { accessToken, refreshToken, currentUser, requiresTwoFactor, twoFactorProvider, twoFactorToken } = response;
    if (accessToken) {
        if (requiresTwoFactor) {
            return { accessToken, requiresTwoFactor, twoFactorProvider, twoFactorToken };
        } else {
            const { token, expiresIn } = accessToken;
            localStorage.setItem("token", token);
            localStorage.setItem("refreshToken", refreshToken);
            localStorage.setItem("tokenExpiresIn", moment().add(expiresIn, "seconds").toISOString());
            localStorage.setItem("currentUser", JSON.stringify(currentUser));
            localStorage.removeItem("refreshTokenRunning");
            return { token, currentUser };
        }
    }
    return { status: "error", message: status === 400 ? response.message : undefined };
};

const generateToken = (body: {}) =>
    fetch(`/${resource}/generateToken`, requestBody(body))
        .then((res) => {
            const { status } = res;
            if (status === 200) {
                return res.json();
            } else if (status === 400) {
                return res.text().then((err) => ({ status: 400, message: err }));
            } else {
                return res;
            }
        })
        .then(loginResponseHandler)
        .catch((err) => console.error({ type: "error", message: "Неудачный запрос", title: "Ошибка.", err }));

type AuthProviderLink = {
    provider: string;
    authLink: string;
};

const getAuthProviderLinks = () =>
    fetch(`/${resource}/getAuthProviderLinks`, {
        cache: "default",
        headers: {
            "X-Device-Id": localStorage.getItem("deviceId") ?? "",
            "X-Requested-With": "XMLHttpRequest",
        },
        method: "GET",
    })
        .then((res) => responseMiddleware(res, {}))
        .catch((err) => console.error({ type: "error", message: "Неудачный запрос", title: "Ошибка.", err }));

const getAuthProviderLink = (provider: string) =>
    getAuthProviderLinks().then((links) =>
        Array.isArray(links) ? links.find((link: AuthProviderLink) => link.provider === provider)?.authLink : undefined,
    );

const authorizeExternal = (params: { provider: string; code: string }) =>
    fetch(`/${resource}/authorizeExternal`, requestBody(params))
        .then((res) => {
            const { status } = res;
            if (status === 200) {
                return res.json();
            }
            if (status === 400) {
                return res.text().then((message) => ({ status, message }));
            }
            return res;
        })
        .then(loginResponseHandler)
        .catch((err) => console.error({ type: "error", message: "Неудачный запрос", title: "Ошибка.", err }));

type LinkExternalAccountResult = {
    success: boolean;
    message?: string;
};

const linkExternalAccount = async (params: { provider: string; code: string }): Promise<LinkExternalAccountResult> => {
    const response = (await request(`${resource}/link`, body(params), { withoutMiddleware: true })) as Response;

    if (response.ok) {
        return { success: true };
    }

    const message = response.status === 400 ? await response.text() : undefined;
    return { success: false, message };
};

const registerOnlyOrganization = (params: {}) =>
    request(`${resource}/registerOnlyOrganization`, body(params), {
        returnErrorText: true,
        withoutErrorAlert: true,
    });

const skipSelfRegistrationDriversAdding = () => request(`${resource}/skipSelfRegistrationDriversAdding`, body({}));
const skipSelfRegistrationTrucksAdding = () => request(`${resource}/skipSelfRegistrationTrucksAdding`, body({}));

const loginTwoFactor = (res: { accessToken?: { token: string }; code: string }) => {
    const { accessToken, ...body } = res;
    return fetch(`/${resource}/loginTwoFactor`, {
        body: JSON.stringify(body),
        method: "POST",
        cache: "default",
        headers: {
            "X-Requested-With": "XMLHttpRequest",
            "Content-Type": "application/json; charset=utf-8",
            "X-Device-Id": localStorage.getItem("deviceId") ?? "",
            Authorization: `Bearer ${accessToken?.token}`,
        },
    })
        .then((res) => {
            const { status } = res;
            if (status === 200) {
                return res.json();
            } else if (status === 400) {
                return res.text().then((err) => ({ status: 400, message: err }));
            } else {
                return res;
            }
        })
        .then(loginResponseHandler)
        .catch((err) => console.error({ type: "error", message: "Неудачный запрос", title: "Ошибка.", err }));
};

export {
    register,
    generateToken,
    getAuthProviderLinks,
    getAuthProviderLink,
    authorizeExternal,
    linkExternalAccount,
    registerOnlyOrganization,
    skipSelfRegistrationDriversAdding,
    skipSelfRegistrationTrucksAdding,
    logout,
    forgotPassword,
    resetPassword,
    acceptInvitation,
    unsubscribeNotifications,
    confirmEmail,
    loginTwoFactor,
    purgeOtherSessions,
};

export type { AuthProviderLink };
